import { useCallback, useEffect, useState } from "react";

import { linkFoto } from "../../services/puerto";

/* Abrir una foto que vive en un bucket PRIVADO, con un link firmado.
 *
 * ── SALIÓ DE ADENTRO DE `PuertoFichaje` ────────────────────────────────────
 *
 * Vivía ahí y estaba bien mientras la única foto del sistema era la del
 * molinete. Con las fotos de las roturas serían **dos definiciones de qué se
 * dice cuando el link no se puede firmar**, y el día que una cambie las dos
 * pantallas contestarían distinto sobre la misma clase de problema. Es la
 * misma razón por la que `Ubicacion`, `ChipUsuario` y `VeredictoRTO` salieron
 * de adentro de las suyas.
 *
 * ── SE ABRE ACÁ MISMO, NO EN OTRA PESTAÑA ──────────────────────────────────
 *
 * Antes era `window.open`. Mirar un vale y volver costaba dos cambios de
 * pestaña, y el que revisa una tabla de treinta cargas hace ese viaje treinta
 * veces — o termina con treinta pestañas abiertas. Ahora la imagen se muestra
 * encima de la tabla y se cierra con la cruz, con un clic afuera o con Escape:
 * los tres, porque cada persona intenta uno distinto primero y el que prueba
 * el que no está implementado cree que la pantalla se colgó.
 *
 * EL CAMBIO ES DEL COMPONENTE Y NO DE UNA PANTALLA, a propósito: partirlo
 * dejaría los vales abriendo un visor y las fotos del puerto abriendo una
 * pestaña, que es exactamente la divergencia que este archivo vino a evitar.
 *
 * ── «NO SE PUDO ABRIR» NO ES «NO HAY FOTO» ─────────────────────────────────
 *
 * `linkFoto` devuelve `null` cuando no pudo firmar, y eso es un problema
 * nuestro; que la fila no traiga ruta es un dato que falta. Dibujados igual,
 * alguien concluye que ese fichaje se cargó sin foto y va a pedirla de nuevo.
 * Sin ruta no se dibuja nada y quien llama decide qué decir en el hueco.
 *
 * ── EL LINK NO SE PIDE HASTA QUE SE APRIETA ────────────────────────────────
 *
 * Firmar al dibujar la tabla serían cien llamadas para las dos fotos que
 * alguien va a mirar — y las otras noventa y ocho se vencerían antes de que
 * las abran.
 *
 * ── O UN LINK QUE YA VIENE LISTO (`link`) ──────────────────────────────────
 *
 * Las fotos que un chofer mandó y te derivaron (Mis pendientes) vienen con el
 * link del bot —`/foto/aviso/<id>/<firma>`—, que no vence y firma uno de
 * Supabase recién cuando se abre. Quien la recibe no tiene por qué tener
 * llave del bucket de novedades: con `link` no se firma nada acá, se abre el
 * mismo visor con eso.
 */
const FotoPrivada = ({ ruta, bucket, link = "", etiqueta = "ver foto", titulo = "" }) => {
  const [estado, setEstado] = useState("");
  const [url, setUrl] = useState(null);

  const cerrar = useCallback(() => setUrl(null), []);

  /* Escape cierra. El listener se pone SÓLO mientras hay algo abierto: uno
   * permanente por cada foto de la tabla serían treinta escuchando teclas
   * para que actúe la única que está abierta. */
  useEffect(() => {
    if (!url) return undefined;
    const alTeclado = (e) => {
      if (e.key === "Escape") cerrar();
    };
    window.addEventListener("keydown", alTeclado);
    return () => window.removeEventListener("keydown", alTeclado);
  }, [url, cerrar]);

  if (!ruta && !link) return null;

  const abrir = async () => {
    if (link) {
      setUrl(link);
      return;
    }
    setEstado("yendo");
    const firmada = await linkFoto(ruta, bucket);
    setEstado(firmada ? "" : "mal");
    if (firmada) setUrl(firmada);
  };

  return (
    <>
      <button
        type="button"
        className="link-button"
        onClick={abrir}
        disabled={estado === "yendo"}
      >
        {estado === "yendo"
          ? "abriendo…"
          : estado === "mal"
            ? "no se pudo abrir"
            : etiqueta}
      </button>

      {url && (
        // El clic en el fondo cierra; el de la imagen NO —`stopPropagation`—
        // porque agrandar el vale para leer un número y que se cierre solo es
        // la forma más rápida de que nadie use el visor.
        <div
          className="foto-visor"
          role="dialog"
          aria-modal="true"
          aria-label={titulo || "Foto"}
          onClick={cerrar}
        >
          <div className="foto-visor__caja" onClick={(e) => e.stopPropagation()}>
            <div className="foto-visor__barra">
              <span className="foto-visor__titulo">{titulo}</span>
              <button
                type="button"
                className="foto-visor__cerrar"
                onClick={cerrar}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <img src={url} alt={titulo || "Foto"} />
            {/* La salida a la pestaña queda: para leer un manuscrito chico hace
                falta el zoom del navegador, que acá adentro no está. */}
            <a
              className="foto-visor__aparte"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrirla en otra pestaña
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default FotoPrivada;
